/**
 * Readers for the **Meta Messenger Platform** message payload envelope.
 *
 * Messenger and Instagram Direct ride the same wire format — `attachments[]`,
 * `quick_replies[]`, `generic.elements[]`, `button`, `receipt`, `referral`,
 * `reply_to.story` — so both channels' renderers were independently
 * re-declaring the same `JSON.parse` + optional-chain helpers
 * (`parseFirstAttachment`, `parseQuickReplies`, `parseGenericElements`,
 * `parseButtonTemplate`, …) over identical shapes. This module is the single
 * typed description of that envelope.
 *
 * Pure and React-free, so the shapes can be unit-tested directly. Channel
 * meaning (what a `story_mention` attachment should look like on screen) stays
 * in each channel's own normalizer — this module only reads the wire format.
 *
 * The field names are Meta's, so they stay snake_case here and are converted at
 * the boundary.
 */

/* ─────────────────────────── wire shapes ─────────────────────────── */

export interface MetaAttachmentPayload {
  url?: string;
  /** The 👍 "like" is delivered as an image attachment carrying a sticker id. */
  sticker_id?: number | string;
  coordinates?: { lat?: number; long?: number };
  title?: string;
}

export interface MetaAttachment {
  type?: string;
  payload?: MetaAttachmentPayload;
}

export interface MetaButton {
  type?: string;
  title?: string;
  url?: string;
}

export interface MetaElement {
  title?: string;
  subtitle?: string;
  image_url?: string;
  buttons?: MetaButton[];
}

export interface MetaReceiptPayload {
  recipient_name?: string;
  order_number?: string;
  currency?: string;
  payment_method?: string;
  summary?: { total_cost?: number };
  elements?: { title?: string; quantity?: number; price?: number }[];
}

export interface MetaReferral {
  source?: string;
  ref?: string;
  ads_context_data?: { ad_title?: string; photo_url?: string };
}

/** Everything this module knows how to read off one message's `payloadJson`. */
export interface MetaMessagePayload {
  attachments?: MetaAttachment[];
  quick_replies?: { title?: string }[];
  generic?: { elements?: MetaElement[] };
  button?: { text?: string; buttons?: MetaButton[] };
  receipt?: MetaReceiptPayload;
  referral?: MetaReferral;
  /** A reply to one of OUR stories: text plus the story's (expiring) media. */
  reply_to?: { story?: { url?: string } };
  product?: { productIds?: string[] };
}

/* ─────────────────────────── readers ─────────────────────────── */

/** Parse a `payloadJson` column. Returns null for absent or malformed JSON —
 *  every caller treats "can't read it" and "not present" identically. */
export function parseMetaPayload(
  payloadJson: string | null | undefined,
): MetaMessagePayload | null {
  if (!payloadJson) return null;
  try {
    return JSON.parse(payloadJson) as MetaMessagePayload;
  } catch {
    return null;
  }
}

/** The first attachment, which is the only one either inbox renders today. */
export function firstAttachment(p: MetaMessagePayload | null): MetaAttachment | null {
  return p?.attachments?.[0] ?? null;
}

/** Quick-reply chip titles, empties dropped. */
export function quickReplyTitles(p: MetaMessagePayload | null): string[] {
  return (p?.quick_replies ?? []).map((q) => q.title ?? '').filter(Boolean);
}

/** Carousel/generic-template cards. */
export function genericElements(p: MetaMessagePayload | null): MetaElement[] {
  return p?.generic?.elements ?? [];
}

/** Button-template body text + buttons. */
export function buttonTemplate(p: MetaMessagePayload | null): {
  text?: string;
  buttons?: MetaButton[];
} | null {
  return p?.button ?? null;
}

export function receiptPayload(p: MetaMessagePayload | null): MetaReceiptPayload | null {
  return p?.receipt ?? null;
}

/** Normalized ad / click-to-message referral. Meta nests the ad copy one level
 *  deeper than the source, so flatten it for the UI. */
export function referral(p: MetaMessagePayload | null): {
  source?: string;
  ref?: string;
  adTitle?: string;
  photoUrl?: string;
} | null {
  const r = p?.referral;
  if (!r) return null;
  return {
    ...(r.source ? { source: r.source } : {}),
    ...(r.ref ? { ref: r.ref } : {}),
    ...(r.ads_context_data?.ad_title ? { adTitle: r.ads_context_data.ad_title } : {}),
    ...(r.ads_context_data?.photo_url ? { photoUrl: r.ads_context_data.photo_url } : {}),
  };
}

/** The story this message replies to (its media lives on an expiring CDN). */
export function storyReply(p: MetaMessagePayload | null): { url?: string } | null {
  return p?.reply_to?.story ?? null;
}

/** Product ids on a product-template share. */
export function productIds(p: MetaMessagePayload | null): string[] {
  return p?.product?.productIds ?? [];
}

/** The sticker id on an attachment, if any — present on the 👍 like. */
export function stickerIdOf(a: MetaAttachment | null): string | number | undefined {
  return a?.payload?.sticker_id;
}

/** `web_url` buttons carry a link; every other kind is a postback with none. */
export function buttonLink(b: MetaButton): string | null {
  return b.type === 'web_url' ? (b.url ?? null) : null;
}
